import { useMemo, useState } from 'react'
import { CAMPAIGN_LIST_MAX_LIMIT, NON_ARCHIVED_CAMPAIGN_STATUSES } from '@/domain/campaign.types'
import { useCampaignsPages } from '@/hooks/useCampaigns'
import { sortedByLabel } from '@/lib/option-order'
import type { AxisOption } from './types'

/**
 * The Campaign attribute's options, paged.
 *
 * `campaign` is a TEXT attribute — it stores a campaign CODE — so the panel used
 * to render a bare text box and expect the code to be typed from memory. It is
 * really a pick from a known set, the same set the New Work Order form offers,
 * so it gets the same list here.
 *
 * Paged by "load more" rather than numbered pages: the list endpoint returns
 * `{ data }` with no total, so a page count would have to be invented. A page
 * that comes back FULL is the only honest signal that more may exist — which
 * also means the last page can be exactly full and offer one more press that
 * returns nothing. That is the cost of not having a total, and it is preferable
 * to a page count that could be wrong.
 *
 * Every page loaded stays in the list, so type-to-filter searches everything
 * fetched so far, not just the newest page.
 *
 * @param enabled - `false` skips the request entirely, for a panel that is not
 *   offering the campaign axis at all.
 */
export function useCampaignOptions(enabled = true): {
	options: AxisOption[]
	hasMore: boolean
	loadMore: () => void
	isLoadingMore: boolean
} {
	const [pageCount, setPageCount] = useState(1)
	const pages = useMemo(
		() => Array.from({ length: pageCount }, (_, i) => ({ page: i + 1, limit: CAMPAIGN_LIST_MAX_LIMIT })),
		[pageCount],
	)
	const results = useCampaignsPages({ status: NON_ARCHIVED_CAMPAIGN_STATUSES }, pages, { enabled })

	const campaigns = results.flatMap((r) => r.data ?? [])
	const isFirstLoad = results.length === 1 && results[0]?.isLoading === true
	// "Loading more" means a page AFTER the first is in flight. The initial load
	// renders no picker rows at all, so calling that "loading more" would light up
	// an affordance for a list the user cannot see yet.
	const isLoadingMore = !isFirstLoad && results.some((r) => r.isLoading)
	// Only a full final page can be followed by another one — but a page still in
	// flight has no `data` yet, and reading that as "no more" would unmount the
	// load-more row at the exact moment it was pressed. An unresolved page counts
	// as more until it comes back and says otherwise.
	const lastPage = results[results.length - 1]?.data
	const hasMore = isLoadingMore || (!!lastPage && lastPage.length === CAMPAIGN_LIST_MAX_LIMIT)

	const options = useMemo(
		() =>
			sortedByLabel(
				// The value is the CODE, since that is what the attribute stores; the
				// name is what a person recognises, so it is the label.
				campaigns.map((c) => ({ value: c.campaignCode, label: c.name })),
			),
		[campaigns],
	)

	return { options, hasMore, loadMore: () => setPageCount((n) => n + 1), isLoadingMore }
}
